import { Modal, Form, Input, Select, Row, Col, message, Tabs, DatePicker, Button, Space, theme, Alert, Typography } from 'antd'
import VoiceTextarea from './VoiceTextarea'
import PhoneInput from './PhoneInput'
import dayjs from 'dayjs'
import { useState, useEffect, useRef } from 'react'
import { parseDataPura } from '@/utils/dateUtils'
import { CameraOutlined, FolderOpenOutlined } from '@ant-design/icons'
import { useCreateCustomer, useUpdateCustomer } from '@/hooks/useCustomers'
import { useCustomerTypes } from '@/hooks/useSystemTables'
import type { Customer } from '@/types'
import AddressAutocompleteInput from '@/components/AddressAutocompleteInput'
import axiosInstance from '@/lib/axios'

interface CustomerFormModalProps {
  open: boolean
  onSuccess: (customer: Customer) => void
  onCancel: () => void
  editingCustomer?: Customer | null
}

const CustomerFormModal = ({ open, onSuccess, onCancel, editingCustomer }: CustomerFormModalProps) => {
  const { token } = theme.useToken()
  const [form] = Form.useForm()
  const [customerType, setCustomerType] = useState<string>('Privato')
  const [activeTab, setActiveTab] = useState('info')
  const createMutation = useCreateCustomer()
  const updateMutation = useUpdateCustomer()
  const { data: customerTypes } = useCustomerTypes()

  // Sezione patente: visibile solo quando valorizzata
  const [hasPatente, setHasPatente] = useState(false)
  // Sezione documento d'identità: visibile solo quando valorizzata
  const [hasDocumento, setHasDocumento] = useState(false)
  // Valori scadenza per avvisi in tempo reale
  const scadenzaPatenteWatch = Form.useWatch('scadenza_patente', form)
  const scadenzaDocumentoWatch = Form.useWatch('scadenza_documento', form)

  // OCR document state
  const [scanning, setScanning] = useState(false)
  const [docModalOpen, setDocModalOpen] = useState(false)
  const [cameraOpen, setCameraOpen] = useState(false)
  const [cameraFacing, setCameraFacing] = useState<'user' | 'environment'>('environment')
  const [capturedFrame, setCapturedFrame] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const startCamera = async (facing: 'user' | 'environment' = cameraFacing) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      })
      streamRef.current = stream
      if (videoRef.current) videoRef.current.srcObject = stream
    } catch {
      message.error('Impossibile accedere alla fotocamera')
      setCameraOpen(false)
    }
  }

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop())
    streamRef.current = null
    setCapturedFrame(null)
  }

  const snapPhoto = () => {
    if (!videoRef.current || !canvasRef.current) return
    const ctx = canvasRef.current.getContext('2d')
    if (!ctx) return
    canvasRef.current.width = videoRef.current.videoWidth
    canvasRef.current.height = videoRef.current.videoHeight
    ctx.drawImage(videoRef.current, 0, 0)
    setCapturedFrame(canvasRef.current.toDataURL('image/jpeg', 0.9))
  }

  const switchCamera = () => {
    const next = cameraFacing === 'environment' ? 'user' : 'environment'
    setCameraFacing(next)
    stopCamera()
    startCamera(next)
  }

  const handleDocumentFile = async (file: File) => {
    setScanning(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await axiosInstance.post('/customers/scan-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      const data = res.data
      const patch: Record<string, any> = {}
      if (data.tipo_documento === 'patente') {
        // Patente: popola i dati anagrafici (campi 1/2/3) e i 4 campi patente (5/9/4b/4c)
        if (data.cognome) patch.cognome = data.cognome
        if (data.nome) patch.nome = data.nome
        if (data.data_nascita) patch.data_nascita = parseDataPura(data.data_nascita)
        if (data.luogo_nascita) patch.luogo_nascita = data.luogo_nascita
        if (data.numero_patente) patch.numero_patente = data.numero_patente
        if (data.categoria_patente) patch.categoria_patente = data.categoria_patente
        if (data.ente_rilascio_patente) patch.ente_rilascio_patente = data.ente_rilascio_patente
        if (data.scadenza_patente) patch.scadenza_patente = parseDataPura(data.scadenza_patente)
        const hasPatentFields = !!(data.numero_patente || data.categoria_patente || data.ente_rilascio_patente || data.scadenza_patente)
        if (hasPatentFields) setHasPatente(true)
        if (Object.keys(patch).length > 0) {
          form.setFieldsValue(patch)
          message.success('Dati estratti dalla patente — verificali prima di salvare')
        } else {
          message.warning('Non è stato possibile leggere i dati dalla patente')
        }
      } else {
        // Carta d'identità / altro: popola i dati anagrafici e i campi documento
        if (data.nome) patch.nome = data.nome
        if (data.cognome) patch.cognome = data.cognome
        if (data.data_nascita) patch.data_nascita = parseDataPura(data.data_nascita)
        if (data.luogo_nascita) patch.luogo_nascita = data.luogo_nascita
        if (data.codice_fiscale) patch.codice_fiscale = data.codice_fiscale
        if (data.indirizzo) patch.indirizzo = data.indirizzo
        if (data.numero_civico) patch.numero_civico = data.numero_civico
        if (data.citta) patch.citta = data.citta
        if (data.provincia) patch.provincia = data.provincia
        if (data.cap) patch.cap = data.cap
        if (data.numero_documento) patch.numero_documento = data.numero_documento
        if (data.scadenza_documento) patch.scadenza_documento = parseDataPura(data.scadenza_documento)
        if (data.numero_documento || data.scadenza_documento) setHasDocumento(true)
        form.setFieldsValue(patch)
        message.success('Dati estratti dal documento')
      }
    } catch {
      message.error('Impossibile leggere il documento')
    } finally {
      setScanning(false)
      setDocModalOpen(false)
    }
  }

  const confirmCameraPhoto = async () => {
    if (!capturedFrame) return
    const blob = await fetch(capturedFrame).then(r => r.blob())
    const file = new File([blob], 'documento_foto.jpg', { type: 'image/jpeg' })
    stopCamera()
    setCameraOpen(false)
    handleDocumentFile(file)
  }

  // Quando si apre il modal o quando editingCustomer cambia, impostare il tipo correttamente
  useEffect(() => {
    if (open && editingCustomer) {
      // Converti "azienda" -> "Azienda", "privato" -> "Privato"
      const capitalizedType = editingCustomer.tipo
        ? editingCustomer.tipo.charAt(0).toUpperCase() + editingCustomer.tipo.slice(1).toLowerCase()
        : 'Privato'
      setCustomerType(capitalizedType)
      // Mostra sezione patente se il cliente ha almeno un campo valorizzato
      setHasPatente(
        !!(editingCustomer.numero_patente || editingCustomer.categoria_patente ||
           editingCustomer.ente_rilascio_patente || editingCustomer.scadenza_patente)
      )
      // Mostra sezione documento d'identità se valorizzata
      setHasDocumento(!!(editingCustomer.numero_documento || editingCustomer.scadenza_documento))
      // Popola tutti i campi del form con i dati aggiornati del cliente
      form.setFieldsValue({
        tipo: capitalizedType,
        ragione_sociale: editingCustomer.ragione_sociale,
        nome: editingCustomer.nome,
        cognome: editingCustomer.cognome,
        data_nascita: parseDataPura(editingCustomer.data_nascita),
        luogo_nascita: editingCustomer.luogo_nascita,
        email: editingCustomer.email,
        telefono: editingCustomer.telefono,
        cellulare: editingCustomer.cellulare,
        contatto1_nome: (editingCustomer as any).contatto1_nome,
        contatto1_telefono: (editingCustomer as any).contatto1_telefono,
        contatto1_email: (editingCustomer as any).contatto1_email,
        contatto2_nome: (editingCustomer as any).contatto2_nome,
        contatto2_telefono: (editingCustomer as any).contatto2_telefono,
        contatto2_email: (editingCustomer as any).contatto2_email,
        codice_fiscale: editingCustomer.codice_fiscale,
        partita_iva: editingCustomer.partita_iva,
        numero_documento: editingCustomer.numero_documento,
        scadenza_documento: parseDataPura(editingCustomer.scadenza_documento),
        numero_patente: editingCustomer.numero_patente,
        categoria_patente: editingCustomer.categoria_patente,
        ente_rilascio_patente: editingCustomer.ente_rilascio_patente,
        scadenza_patente: parseDataPura(editingCustomer.scadenza_patente),
        indirizzo: editingCustomer.indirizzo,
        numero_civico: editingCustomer.numero_civico,
        citta: editingCustomer.citta,
        provincia: editingCustomer.provincia,
        cap: editingCustomer.cap,
        note: editingCustomer.note,
      })
    } else if (open && !editingCustomer) {
      form.resetFields()
    } else if (!open) {
      setCustomerType('Privato')
      setHasPatente(false)
      setHasDocumento(false)
    }
  }, [open, editingCustomer, form])

  const handleSubmit = async (values: any) => {
    try {
      // Normalizza tipo a lowercase per il backend
      let normalized = {
        ...values,
        tipo: values.tipo ? values.tipo.toLowerCase() : undefined,
        // Converti dayjs → stringa ISO per il backend
        data_nascita: values.data_nascita ? values.data_nascita.format('YYYY-MM-DD') : undefined,
        scadenza_patente: values.scadenza_patente ? values.scadenza_patente.format('YYYY-MM-DD') : undefined,
        scadenza_documento: values.scadenza_documento ? values.scadenza_documento.format('YYYY-MM-DD') : undefined,
      }

      // Filtra campi in base al tipo cliente
      if (normalized.tipo === 'azienda') {
        // Per aziende: rimuovi nome e cognome, tieni ragione_sociale
        delete normalized.nome
        delete normalized.cognome
      } else if (normalized.tipo === 'privato') {
        // Per privati: rimuovi ragione_sociale, tieni nome e cognome
        delete normalized.ragione_sociale
      }
      
      let response: Customer
      if (editingCustomer) {
        response = await updateMutation.mutateAsync({ id: editingCustomer.id, data: normalized })
        message.success('Cliente aggiornato con successo')
      } else {
        response = await createMutation.mutateAsync(normalized)
        message.success('Cliente creato con successo')
      }
      
      form.resetFields()
      setCustomerType('Privato')
      setActiveTab('info')
      setHasPatente(false)
      setHasDocumento(false)
      onSuccess(response)
      onCancel()
    } catch (error) {
      message.error(editingCustomer ? 'Errore durante l\'aggiornamento' : 'Errore durante il salvataggio')
    }
  }

  const fieldToTab: Record<string, string> = {
    tipo: 'info', ragione_sociale: 'info', nome: 'info', cognome: 'info',
    data_nascita: 'info', luogo_nascita: 'info',
    email: 'contact', telefono: 'contact', cellulare: 'contact',
    contatto1_nome: 'contact', contatto1_telefono: 'contact', contatto1_email: 'contact',
    contatto2_nome: 'contact', contatto2_telefono: 'contact', contatto2_email: 'contact',
    codice_fiscale: 'docs', partita_iva: 'docs',
    numero_documento: 'docs', scadenza_documento: 'docs',
    numero_patente: 'docs', categoria_patente: 'docs',
    ente_rilascio_patente: 'docs', scadenza_patente: 'docs',
    indirizzo: 'address', numero_civico: 'address', citta: 'address', provincia: 'address', cap: 'address',
    note: 'notes',
  }

  const handleFinishFailed = ({ errorFields }: { errorFields: { name: (string | number)[] }[] }) => {
    const firstField = String(errorFields[0]?.name?.[0] ?? '')
    const tab = fieldToTab[firstField]
    if (tab) setActiveTab(tab)
  }

  const handleCancel = () => {
    form.resetFields()
    setCustomerType('Privato')
    setActiveTab('info')
    setHasPatente(false)
    setHasDocumento(false)
    onCancel()
  }

  return (
    <Modal
      title={editingCustomer ? 'Modifica Cliente' : 'Nuovo Cliente'}
      open={open}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      confirmLoading={editingCustomer ? updateMutation.isPending : createMutation.isPending}
      width="min(700px, 95vw)"
      okText="Salva"
      cancelText="Annulla"
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
        onFinish={handleSubmit}
        onFinishFailed={handleFinishFailed}
        initialValues={{ tipo: 'Privato' }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'info',
              label: 'Informazioni',
              children: (
                <>
                  <Form.Item
                    name="tipo"
                    label="Tipo Cliente"
                    rules={[{ required: true, message: 'Seleziona il tipo' }]}
                    required
                  >
                    <Select placeholder="Seleziona un tipo" onChange={(value) => setCustomerType(value)}>
                      {customerTypes?.filter(ct => ct.attivo).map(ct => (
                        <Select.Option key={ct.id} value={ct.nome}>
                          {ct.nome}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  {customerType?.toLowerCase() === 'azienda' ? (
                    <Form.Item
                      name="ragione_sociale"
                      label="Ragione Sociale"
                      rules={[{ required: true, message: 'Inserisci la ragione sociale' }]}
                      required
                    >
                      <Input placeholder="es. Carrozzeria Rossi S.r.l." />
                    </Form.Item>
                  ) : (
                    <>
                      <Form.Item style={{ marginBottom: 12 }}>
                        <Button
                          icon={<CameraOutlined />}
                          loading={scanning}
                          onClick={() => setDocModalOpen(true)}
                          size="small"
                        >
                          Scansiona documento d&apos;identità
                        </Button>
                      </Form.Item>
                      <Row gutter={12}>
                        <Col span={12}>
                          <Form.Item
                            name="nome"
                            label="Nome"
                            rules={[{ required: true, message: 'Inserisci il nome' }]}
                            required
                          >
                            <Input size="small" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="cognome"
                            label="Cognome"
                            rules={[{ required: true, message: 'Inserisci il cognome' }]}
                            required
                          >
                            <Input size="small" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={12}>
                        <Col span={12}>
                          <Form.Item
                            name="data_nascita"
                            label="Data di nascita"
                          >
                            <DatePicker
                              size="small"
                              style={{ width: '100%' }}
                              format="DD/MM/YYYY"
                              placeholder="gg/mm/aaaa"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="luogo_nascita"
                            label="Luogo di nascita"
                          >
                            <Input size="small" placeholder="es. Roma" />
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  )}
                </>
              ),
            },
            {
              key: 'contact',
              label: 'Contatti',
              children: (
                <>
                  {/* Contatti principali */}
                  <Row gutter={8} style={{ marginBottom: 0 }}>
                    <Col span={10}>
                      <Form.Item name="email" style={{ marginBottom: 8 }} rules={[{ type: 'email', message: 'Email non valida' }]}>
                        <Input size="small" placeholder="Email principale" />
                      </Form.Item>
                    </Col>
                    <Col span={7}>
                      <Form.Item name="telefono" style={{ marginBottom: 8 }}>
                        <PhoneInput size="small" placeholder="Fisso" />
                      </Form.Item>
                    </Col>
                    <Col span={7}>
                      <Form.Item name="cellulare" style={{ marginBottom: 8 }}>
                        <PhoneInput size="small" placeholder="Cellulare" />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Contatto 1 — 2 righe */}
                  <div style={{ borderTop: `1px solid ${token.colorBorderSecondary}`, marginTop: 4, paddingTop: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 11, color: token.colorTextSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>Contatto 1</span>
                  </div>
                  <Row gutter={8}>
                    <Col span={10}>
                      <Form.Item name="contatto1_nome" style={{ marginBottom: 4 }}>
                        <Input size="small" placeholder="Nome e cognome" />
                      </Form.Item>
                    </Col>
                    <Col span={7}>
                      <Form.Item name="contatto1_telefono" style={{ marginBottom: 4 }}>
                        <PhoneInput size="small" placeholder="Telefono" />
                      </Form.Item>
                    </Col>
                    <Col span={7}>
                      <Form.Item name="contatto1_email" style={{ marginBottom: 4 }} rules={[{ type: 'email', message: 'Email non valida' }]}>
                        <Input size="small" placeholder="Email" />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Contatto 2 — 2 righe */}
                  <div style={{ borderTop: `1px solid ${token.colorBorderSecondary}`, marginTop: 4, paddingTop: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 11, color: token.colorTextSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>Contatto 2</span>
                  </div>
                  <Row gutter={8}>
                    <Col span={10}>
                      <Form.Item name="contatto2_nome" style={{ marginBottom: 4 }}>
                        <Input size="small" placeholder="Nome e cognome" />
                      </Form.Item>
                    </Col>
                    <Col span={7}>
                      <Form.Item name="contatto2_telefono" style={{ marginBottom: 4 }}>
                        <PhoneInput size="small" placeholder="Telefono" />
                      </Form.Item>
                    </Col>
                    <Col span={7}>
                      <Form.Item name="contatto2_email" style={{ marginBottom: 4 }} rules={[{ type: 'email', message: 'Email non valida' }]}>
                        <Input size="small" placeholder="Email" />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              ),
            },
            {
              key: 'docs',
              label: 'Documenti',
              children: (
                <>
                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item
                        name="codice_fiscale"
                        label="Codice Fiscale"
                        tooltip="Opzionale"
                      >
                        <Input size="small" placeholder="RSSMRA80A01H501U" autoComplete="one-time-code" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="partita_iva"
                        label="Partita IVA"
                        tooltip="Per aziende"
                      >
                        <Input size="small" placeholder="11 cifre" maxLength={11} autoComplete="one-time-code" />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Sezione documento d'identità — visibile solo se valorizzata */}
                  {hasDocumento && (
                    <>
                      <div style={{ borderTop: `1px solid ${token.colorBorderSecondary}`, marginTop: 4, paddingTop: 8, marginBottom: 10 }}>
                        <Typography.Text style={{ fontSize: 11, color: token.colorTextSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                          Documento d&apos;identità
                        </Typography.Text>
                      </div>
                      {/* Avviso scadenza */}
                      {scadenzaDocumentoWatch && dayjs().isAfter(scadenzaDocumentoWatch) && (
                        <Alert
                          type="warning"
                          showIcon
                          message={`Documento scaduto il ${scadenzaDocumentoWatch.format('DD/MM/YYYY')}`}
                          style={{ marginBottom: 10 }}
                        />
                      )}
                      <Row gutter={12}>
                        <Col span={12}>
                          <Form.Item name="numero_documento" label="Numero documento">
                            <Input size="small" placeholder="es. CA00000AA" autoComplete="one-time-code" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="scadenza_documento" label="Scadenza">
                            <DatePicker size="small" style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="gg/mm/aaaa" />
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  )}

                  {/* Sezione patente — visibile solo se valorizzata (via OCR o in modifica) */}
                  {hasPatente && (
                    <>
                      <div style={{ borderTop: `1px solid ${token.colorBorderSecondary}`, marginTop: 4, paddingTop: 8, marginBottom: 10 }}>
                        <Typography.Text style={{ fontSize: 11, color: token.colorTextSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                          Patente di guida
                        </Typography.Text>
                      </div>
                      {/* Avviso scadenza */}
                      {scadenzaPatenteWatch && dayjs().isAfter(scadenzaPatenteWatch) && (
                        <Alert
                          type="warning"
                          showIcon
                          message={`Patente scaduta il ${scadenzaPatenteWatch.format('DD/MM/YYYY')}`}
                          style={{ marginBottom: 10 }}
                        />
                      )}
                      <Row gutter={12}>
                        <Col span={12}>
                          <Form.Item name="numero_patente" label="Numero patente">
                            <Input size="small" placeholder="es. AA0000000A" autoComplete="one-time-code" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="categoria_patente" label="Categoria">
                            <Input size="small" placeholder="es. B" autoComplete="one-time-code" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={12}>
                        <Col span={12}>
                          <Form.Item name="ente_rilascio_patente" label="Ente di rilascio">
                            <Input size="small" placeholder="es. Prefettura di Roma" autoComplete="one-time-code" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="scadenza_patente" label="Scadenza">
                            <DatePicker size="small" style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="gg/mm/aaaa" />
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  )}
                </>
              ),
            },
            {
              key: 'address',
              label: 'Indirizzo',
              children: (
                <>
                  <Row gutter={8}>
                    <Col span={18}>
                      <Form.Item name="indirizzo" label="Indirizzo">
                        <AddressAutocompleteInput
                          onPlaceSelect={(parsed) => {
                            form.setFieldsValue({
                              indirizzo: parsed.indirizzo,
                              numero_civico: parsed.numero_civico ?? '',
                              citta: parsed.citta,
                              provincia: parsed.provincia,
                              cap: parsed.cap,
                            })
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="numero_civico" label="N° Civico">
                        <Input size="small" placeholder="es. 10" maxLength={20} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={8}>
                    <Col span={12}>
                      <Form.Item name="citta" label="Città">
                        <Input size="small" />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="provincia" label="Prov">
                        <Input size="small" maxLength={2} />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="cap" label="CAP">
                        <Input size="small" />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              ),
            },
            {
              key: 'notes',
              label: 'Note',
              children: (
                <Form.Item name="note" label="">
                  <VoiceTextarea rows={3} minHeight={90} maxHeight={200} placeholder="Aggiungi note..." label="Note cliente" compact />
                </Form.Item>
              ),
            },
          ]}
        />
      </Form>

      {/* Document acquisition choice */}
      <Modal
        open={docModalOpen}
        title="Acquisisci documento d'identità"
        onCancel={() => setDocModalOpen(false)}
        footer={null}
        width={340}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button
            block
            icon={<CameraOutlined />}
            onClick={() => {
              setDocModalOpen(false)
              setCameraOpen(true)
              startCamera()
            }}
          >
            Scatta foto con fotocamera
          </Button>
          <Button
            block
            icon={<FolderOpenOutlined />}
            onClick={() => fileInputRef.current?.click()}
          >
            Carica file (JPG, PNG, PDF)
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            style={{ display: 'none' }}
            onChange={e => {
              const f = e.target.files?.[0]
              if (f) { setDocModalOpen(false); handleDocumentFile(f) }
              e.target.value = ''
            }}
          />
        </Space>
      </Modal>

      {/* Camera modal */}
      <Modal
        open={cameraOpen}
        title="Fotocamera — documento d'identità"
        footer={null}
        onCancel={() => { stopCamera(); setCameraOpen(false) }}
        afterOpenChange={isOpen => { if (isOpen) startCamera() }}
        width={560}
      >
        {!capturedFrame ? (
          <>
            <video ref={videoRef} autoPlay playsInline style={{ width: '100%', borderRadius: 4 }} />
            <Space style={{ marginTop: 8 }}>
              <Button onClick={snapPhoto} type="primary">Scatta</Button>
              <Button onClick={switchCamera}>Cambia fotocamera</Button>
            </Space>
          </>
        ) : (
          <>
            <img src={capturedFrame} alt="preview" style={{ width: '100%', borderRadius: 4 }} />
            <Space style={{ marginTop: 8 }}>
              <Button onClick={confirmCameraPhoto} type="primary" loading={scanning}>Usa questa foto</Button>
              <Button onClick={() => setCapturedFrame(null)}>Riprova</Button>
            </Space>
          </>
        )}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </Modal>
    </Modal>
  )
}

export default CustomerFormModal

